/**
 * Plain-English translation layer for the Agent Activity page.
 *
 * Maps raw agent API action names to human sentences, a context domain
 * (what part of the CRM the agent touched), and a kind (read / write / poll /
 * report) so the live feed can read like a story instead of an API log.
 */

export type ActivityKind = 'read' | 'write' | 'poll' | 'report';

export type ActivityDomain =
  | 'leads'
  | 'calendar'
  | 'chat'
  | 'trips'
  | 'deals'
  | 'projects'
  | 'files'
  | 'tasks'
  | 'reports'
  | 'email'
  | 'portals'
  | 'finance'
  | 'sales'
  | 'meetings'
  | 'cost'
  | 'system';

export interface HumanizedEvent {
  /** Verb phrase, lowercase, no subject — e.g. "replied in travel chat". */
  sentence: string;
  domain: ActivityDomain;
  kind: ActivityKind;
}

interface ActionEntry {
  s: string;
  d: ActivityDomain;
  k: ActivityKind;
}

const e = (s: string, d: ActivityDomain, k: ActivityKind): ActionEntry => ({ s, d, k });

/** Every known agent API action → sentence/domain/kind. */
const ACTION_MAP: Record<string, ActionEntry> = {
  // ── Gateway meta / telemetry ──
  status: e('checked in', 'system', 'poll'),
  health: e('checked in', 'system', 'poll'),
  catalog: e('read the API catalog', 'system', 'read'),
  getApi: e('read an API guide', 'system', 'read'),
  reportUsage: e('reported LLM spend', 'cost', 'report'),
  /** Gateway shell only — real work is the per-activity `source: reported` events. */
  reportActivity: e('synced activity', 'system', 'poll'),
  reportRequests: e('filed a coordination request', 'system', 'report'),
  reportCrmChangeRequest: e('filed a coordination request', 'system', 'report'),

  // ── Travel / Calendar worker (travelAgent → Calendar) ──
  requestTripTravelSearch: e('queued a travel price search', 'trips', 'write'),
  requestTripCalendarSync: e('queued a trip calendar sync', 'trips', 'write'),
  listTripTravelSearchRequests: e('checked for travel search work', 'trips', 'poll'),
  listTripCalendarSyncRequests: e('checked for calendar sync work', 'trips', 'poll'),
  claimTripTravelSearch: e('picked up a travel search', 'trips', 'write'),
  claimTripCalendarSync: e('picked up a calendar sync', 'calendar', 'write'),
  getTripForTravelSearch: e('read trip details for a travel search', 'trips', 'read'),
  getTripForCalendarSync: e('read trip details for a calendar sync', 'trips', 'read'),
  submitTripTravelSearchResults: e('delivered travel search results', 'trips', 'write'),
  submitTripCalendarSyncResult: e('finished syncing a trip to the calendar', 'calendar', 'write'),
  failTripTravelSearch: e('reported a failed travel search', 'trips', 'write'),
  failTripCalendarSync: e('reported a failed calendar sync', 'calendar', 'write'),
  requestTripPlanFromPrompt: e('queued a trip plan', 'trips', 'write'),
  listTripPlanRequests: e('checked for trip plan work', 'trips', 'poll'),
  claimTripPlanRequest: e('picked up a trip plan request', 'trips', 'write'),
  getTripPlanRequest: e('read a trip plan request', 'trips', 'read'),
  getTripPlannerThread: e('read a trip planner thread', 'trips', 'read'),
  submitTripPlanDraft: e('drafted a trip plan', 'trips', 'write'),
  failTripPlanRequest: e('reported a failed trip plan', 'trips', 'write'),
  sendTravelChatMessage: e('got a new chat message', 'chat', 'write'),
  listTravelChatMessages: e('checked for new chat messages', 'chat', 'poll'),
  claimTravelChatMessage: e('picked up a chat message', 'chat', 'write'),
  getTravelChatThread: e('read a chat thread', 'chat', 'read'),
  postTravelChatReply: e('replied in chat', 'chat', 'write'),
  failTravelChatMessage: e('reported a failed chat reply', 'chat', 'write'),

  // ── Executive calendar callables ──
  executiveCalendarListManagedExecutives: e('looked up managed executives', 'calendar', 'read'),
  executiveCalendarListCalendars: e('listed executive calendars', 'calendar', 'read'),
  executiveCalendarListEvents: e('read calendar events', 'calendar', 'read'),
  executiveCalendarInsertEvent: e('added a calendar event', 'calendar', 'write'),
  executiveCalendarPatchEvent: e('updated a calendar event', 'calendar', 'write'),
  executiveCalendarDeleteEvent: e('removed a calendar event', 'calendar', 'write'),
  executiveCalendarGetDrivingDuration: e('checked driving time between events', 'calendar', 'read'),
  executiveCalendarInsertEventGroup: e('added a trip to the calendar', 'calendar', 'write'),
  executiveCalendarSyncEventGroup: e('synced a trip on the calendar', 'calendar', 'write'),
  executiveCalendarDeleteEventGroup: e('removed a trip from the calendar', 'calendar', 'write'),

  // ── Marketing ──
  upsertReport: e('filed the daily marketing report', 'reports', 'write'),
  getReport: e('read a marketing report', 'reports', 'read'),
  listReports: e('browsed marketing reports', 'reports', 'read'),
  upsertOpportunityReport: e('filed the opportunity radar', 'reports', 'write'),
  getOpportunityReport: e('read the opportunity radar', 'reports', 'read'),
  deleteOpportunityReport: e('removed an opportunity report', 'reports', 'write'),
  listOpportunityReports: e('browsed opportunity reports', 'reports', 'read'),
  requestLeadEnrichment: e('queued a lead enrichment', 'leads', 'write'),
  listLeadEnrichmentRequests: e('checked for enrichment work', 'leads', 'poll'),
  claimLeadEnrichment: e('picked up a lead enrichment', 'leads', 'write'),
  getLeadForEnrichment: e('read a lead for enrichment', 'leads', 'read'),
  getEnrichmentKeys: e('read enrichment settings', 'system', 'read'),
  upsertLeadEnrichment: e('wrote enrichment onto a lead', 'leads', 'write'),
  completeLeadEnrichment: e('finished enriching a lead', 'leads', 'write'),
  failLeadEnrichment: e('reported a failed enrichment', 'leads', 'write'),

  // ── Bidding / Files ──
  requestLeadBiddingAnalysis: e('queued a bidding analysis', 'leads', 'write'),
  listLeadBiddingAnalysisRequests: e('checked for bidding work', 'leads', 'poll'),
  claimLeadBiddingAnalysis: e('picked up a bidding analysis', 'leads', 'write'),
  getLeadForBiddingAnalysis: e('read a lead for bidding analysis', 'leads', 'read'),
  getLeadDocumentsForAnalysis: e('pulled lead documents for analysis', 'files', 'read'),
  getProposalContext: e('read proposal context', 'deals', 'read'),
  getBiddingKeys: e('read bidding settings', 'system', 'read'),
  searchLeads: e('searched leads', 'leads', 'read'),
  upsertLeadBiddingAnalysis: e('wrote a bidding analysis', 'leads', 'write'),
  upsertAgentAnnotations: e('annotated documents', 'files', 'write'),
  upsertDocumentSynopsis: e('wrote a document synopsis', 'files', 'write'),
  getAgentAnnotationsForAnalysis: e('read document annotations', 'files', 'read'),
  getTakeoffContext: e('read takeoff context', 'deals', 'read'),
  exportTakeoffTrainingExamples: e('exported takeoff training examples', 'deals', 'read'),
  proposeTakeoffProposals: e('proposed takeoff line items', 'deals', 'write'),
  approveTakeoffProposals: e('approved takeoff proposals', 'deals', 'write'),
  failLeadBiddingAnalysis: e('reported a failed bidding analysis', 'leads', 'write'),
  requestProposalReview: e('queued a proposal review', 'deals', 'write'),
  listProposalReviewRequests: e('checked for proposal review work', 'deals', 'poll'),
  claimProposalReview: e('picked up a proposal review', 'deals', 'write'),
  failProposalReview: e('reported a failed proposal review', 'deals', 'write'),
  upsertProposalReview: e('delivered a proposal review', 'deals', 'write'),

  // ── Deals / Projects exports ──
  exportSnapshot: e('exported a data snapshot', 'deals', 'read'),
  listDealsSince: e('read recent deals', 'deals', 'read'),
  getDeal: e('read a deal', 'deals', 'read'),
  listProjectsSince: e('read recent projects', 'projects', 'read'),
  getProject: e('read a project', 'projects', 'read'),

  // ── Finance ──
  listExpenses: e('pulled expenses', 'finance', 'read'),
  expenseSummary: e('summarized expenses', 'finance', 'read'),
  listInvoices: e('pulled invoices', 'finance', 'read'),
  arAging: e('checked AR aging', 'finance', 'read'),
  listPayments: e('pulled payments', 'finance', 'read'),
  revenueSummary: e('summarized revenue', 'finance', 'read'),
  listSalesOrders: e('pulled sales orders', 'finance', 'read'),
  pipelineValue: e('checked pipeline value', 'finance', 'read'),
  projectPL: e('ran a project P&L', 'finance', 'read'),
  projectCosts: e('pulled project costs', 'finance', 'read'),
  cashPosition: e('checked cash position', 'finance', 'read'),
  getExpense: e('read an expense', 'finance', 'read'),
  listPurchaseOrders: e('listed purchase orders', 'finance', 'read'),
  getPurchaseOrder: e('read a purchase order', 'finance', 'read'),
  suggestPurchaseOrders: e('suggested purchase orders', 'finance', 'read'),
  assignExpense: e('assigned an expense', 'finance', 'write'),
  linkExpenseToPurchaseOrder: e('linked an expense to a purchase order', 'finance', 'write'),
  unlinkExpenseFromPurchaseOrder: e('unlinked an expense from a purchase order', 'finance', 'write'),
  assignPayment: e('assigned a payment', 'finance', 'write'),

  // ── Meeting notes ──
  searchProjects: e('searched projects', 'projects', 'read'),
  getProjectContext: e('read project context', 'projects', 'read'),
  upsertMeetingNote: e('filed a meeting note', 'meetings', 'write'),
  getMeetingNote: e('read a meeting note', 'meetings', 'read'),

  // ── Portal activity ──
  listActivitySince: e('checked portal activity', 'portals', 'poll'),
  getPortalActivity: e('read a portal timeline', 'portals', 'read'),
  listPortals: e('listed client portals', 'portals', 'read'),
  listReengagements: e('checked for portal re-engagements', 'portals', 'read'),

  // ── Ops tasks ──
  createOpsTask: e('created an ops task', 'tasks', 'write'),
  listOpsTasks: e('browsed ops tasks', 'tasks', 'read'),
  getOpsTask: e('read an ops task', 'tasks', 'read'),
  updateOpsTask: e('updated an ops task', 'tasks', 'write'),
  listOpsAssignees: e('looked up task assignees', 'tasks', 'read'),

  // ── Sales ──
  upsertWinLossDigest: e('filed the win/loss digest', 'sales', 'write'),
  getWinLossDigest: e('read the win/loss digest', 'sales', 'read'),
  listWinLossDigests: e('browsed win/loss digests', 'sales', 'read'),

  // ── Exec inbox ──
  stages: e('read inbox stages', 'email', 'read'),
  threads: e('read inbox threads', 'email', 'read'),
  thread: e('read an email thread', 'email', 'read'),
  search: e('searched the inbox', 'email', 'read'),
  sync: e('synced the inbox', 'email', 'write'),
  changes: e('checked for inbox changes', 'email', 'poll'),
  classify: e('classified emails', 'email', 'write'),
  draft: e('drafted an email reply', 'email', 'write'),
  link: e('linked an email to the CRM', 'email', 'write'),
  summarize: e('summarized an email thread', 'email', 'read'),
  send: e('sent an email', 'email', 'write'),
  feedback: e('recorded triage feedback', 'email', 'write'),
  log: e('logged inbox work', 'email', 'write'),
  briefingContext: e('gathered briefing context', 'email', 'read'),
  publishBriefing: e('published the daily briefing', 'email', 'write'),
  getBriefing: e('read the daily briefing', 'email', 'read'),

  // ── CRM lead API ──
  createLead: e('created a lead', 'leads', 'write'),
  createLeads: e('created leads', 'leads', 'write'),
  getLead: e('read a lead', 'leads', 'read'),
  listLeads: e('browsed leads', 'leads', 'read'),
  updateLead: e('updated a lead', 'leads', 'write'),
  deleteLead: e('deleted a lead', 'leads', 'write'),
};

/** camelCase → spaced words fallback. */
function prettifyAction(action: string): string {
  return action
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();
}

function inferKind(action: string): ActivityKind {
  if (/^(list|get|search|read|export|check)/i.test(action)) return 'read';
  if (/^(upsert|insert|create|update|patch|delete|remove|post|submit|send|claim|fail|complete|approve|propose|publish|sync)/i.test(action)) {
    return 'write';
  }
  // Dotted slugs from reportActivity (e.g. slack.calendar.reply) are real work.
  if (action.includes('.')) return 'write';
  return 'read';
}

function domainFromSkillParams(
  paramsPreview?: Record<string, string | number | boolean> | null,
): ActivityDomain | null {
  if (!paramsPreview) return null;
  const raw = [paramsPreview.skillsUsed, paramsPreview.skill]
    .filter(v => typeof v === 'string')
    .join(' ')
    .toLowerCase();
  if (!raw) return null;
  if (/calendar|travel/.test(raw)) return 'calendar';
  if (/inbox|email|gmail/.test(raw)) return 'email';
  if (/bidding|files/.test(raw)) return 'files';
  if (/deal/.test(raw)) return 'deals';
  if (/project/.test(raw)) return 'projects';
  if (/meeting|fathom|notes/.test(raw)) return 'meetings';
  if (/market/.test(raw)) return 'leads';
  if (/finance/.test(raw)) return 'finance';
  if (/sales/.test(raw)) return 'sales';
  if (/portal/.test(raw)) return 'portals';
  if (/ops|task/.test(raw)) return 'tasks';
  return null;
}

function domainFromHaystack(hay: string): ActivityDomain | null {
  const h = hay.toLowerCase();
  if (/slack|chat|dm\b/.test(h)) return 'chat';
  if (/calendar|trip|travel|event/.test(h)) return 'calendar';
  if (/email|gmail|inbox|briefing|draft/.test(h)) return 'email';
  if (/deal|proposal/.test(h)) return 'deals';
  if (/lead|enrich/.test(h)) return 'leads';
  if (/project/.test(h)) return 'projects';
  if (/meeting|fathom/.test(h)) return 'meetings';
  if (/financ|invoice|payment/.test(h)) return 'finance';
  return null;
}

/**
 * Translate a raw activity event into a human phrase.
 * Prefer the agent-authored `task` whenever present — that is the work, on or
 * off the CRM. Gateway shells (reportActivity / reportUsage) stay quiet.
 */
export function humanizeEvent(
  action: string,
  paramsPreview?: Record<string, string | number | boolean> | null,
  source?: string,
): HumanizedEvent {
  const task =
    paramsPreview && typeof paramsPreview.task === 'string' ? paramsPreview.task.trim() : '';
  const known = ACTION_MAP[action];
  const hay = `${action} ${task}`;

  // Self-reported work: show the task, and treat it as real activity (not telemetry).
  if (source === 'reported' || (task && action !== 'reportUsage' && action !== 'reportActivity')) {
    const domain =
      (known && known.d !== 'system' ? known.d : null) ||
      domainFromSkillParams(paramsPreview) ||
      domainFromHaystack(hay) ||
      'system';
    const knownKind = known?.k && known.k !== 'report' && known.k !== 'poll' ? known.k : null;
    // Task text used as action ("Matthew asked…") looks like a "read" to inferKind —
    // reported rows are work the agent did unless the slug is clearly a get/list.
    const slugLooksLikeRead = /^(list|get|search|read|export|check)[A-Z._]/i.test(action);
    const kind: ActivityKind = knownKind || (slugLooksLikeRead ? 'read' : 'write');
    return {
      sentence: task || known?.s || prettifyAction(action) || 'did something',
      domain,
      kind,
    };
  }

  if (known) return { sentence: known.s, domain: known.d, kind: known.k };
  return {
    sentence: prettifyAction(action) || 'did something',
    domain: domainFromHaystack(action) || 'system',
    kind: inferKind(action),
  };
}

/** True for heartbeat-style traffic that should collapse in the feed / skip the canvas. */
export function isPollEvent(action: string, source?: string): boolean {
  // Per-activity rows from reportActivity are real work, never polls.
  if (source === 'reported') return false;
  // Gateway shells: the batch call itself is not the work.
  if (action === 'reportUsage' || action === 'reportActivity') return true;
  const known = ACTION_MAP[action];
  if (known) return known.k === 'poll';
  return false;
}

/** Display labels for domains (feed chips). */
export const DOMAIN_LABELS: Record<ActivityDomain, string> = {
  leads: 'Leads',
  calendar: 'Calendar',
  chat: 'Chat',
  trips: 'Trips',
  deals: 'Deals',
  projects: 'Projects',
  files: 'Files',
  tasks: 'Tasks',
  reports: 'Reports',
  email: 'Email',
  portals: 'Portals',
  finance: 'Finance',
  sales: 'Sales',
  meetings: 'Meetings',
  cost: 'Cost',
  system: 'System',
};

/** What each logical agent can touch — shown as chips on the status board. */
export const CAPABILITY_CHIPS: Record<string, string[]> = {
  calendarAgent: ['Calendar', 'Trips', 'Chat'],
  execInboxAgent: ['Email', 'Briefings'],
  filesAgent: ['Documents', 'Annotations'],
  marketingAgent: ['Leads', 'Reports'],
  biddingAgent: ['Leads', 'Documents', 'Proposals'],
  dealsAgent: ['Deals'],
  projectsAgent: ['Projects'],
  financeAgent: ['Finance'],
  purchasingAgent: ['Finance', 'POs'],
  meetingNotesAgent: ['Meetings', 'Projects'],
  projectMeetingsAgent: ['Meetings', 'Projects'],
  portalActivityAgent: ['Portals'],
  opsTasksAgent: ['Tasks'],
  salesAgent: ['Sales'],
  companyBrain: ['Briefings', 'Reports'],
};

/** Human sentence for a CRM → agent queue request ("Sarah sent a chat message"). */
export function humanizeQueueRequest(queueLabel: string, requesterName: string | null): string {
  const who = requesterName || 'Someone';
  switch (queueLabel) {
    case 'Chat message':
      return `${who} sent a chat message`;
    case 'Lead enrichment':
      return `${who} requested a lead enrichment`;
    case 'Bidding analysis':
      return `${who} requested a bidding analysis`;
    case 'Proposal review':
      return `${who} requested a proposal review`;
    case 'Travel search':
      return `${who} requested a travel price search`;
    case 'Calendar sync':
      return `${who} requested a trip calendar sync`;
    case 'Trip plan':
      return `${who} requested a trip plan`;
    default:
      return `${who} queued ${queueLabel.toLowerCase()}`;
  }
}
